import type { MutableRefObject } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import type {
  InitialConfigType,
  InitialEditorStateType,
} from '@lexical/react/LexicalComposer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import {
  BeautifulMentionNode,
  BeautifulMentionsPlugin,
} from 'lexical-beautiful-mentions';
import type {
  EditorState,
  LexicalEditor,
  SerializedEditorState,
} from 'lexical';
import { ParagraphNode, TextNode } from 'lexical';
import type { User } from '@/shared/types/user';
import {
  beautifulMentionsTheme,
  CustomMenu,
  CustomMenuItem,
} from './components';

export function Editor({
  content,
  onChange,
  onEmpty,
  clear,
  placeholder,
  users,
  showBorder: _showBorder = true,
}: {
  content?: SerializedEditorState | null;
  onChange?: (content: SerializedEditorState) => void;
  onEmpty?: (value: boolean) => void;
  clear?: MutableRefObject<(() => void) | undefined>;
  placeholder?: string;
  users: User[];
  showBorder?: boolean;
}) {
  const mentionItems = useMemo(() => {
    return {
      '@': users.map((user) => ({ value: user.data.displayName, id: user.id })),
    };
  }, [users]);
  const state = useRef<InitialEditorStateType | undefined>(undefined);
  const updating = useRef(false);
  const editorConfig = useMemo((): InitialConfigType => {
    const initialValue = !content ? undefined : JSON.stringify(content);
    state.current = initialValue;

    return {
      namespace: 'CommentEditor',
      editorState: initialValue,
      onError: (error: Error) => console.error(error),
      nodes: [
        ParagraphNode,
        TextNode,
        // ...createBeautifulMentionNode(
        // CustomMentionComponent,
        // )
        BeautifulMentionNode,
      ],
      theme: {
        beautifulMentions: beautifulMentionsTheme,
      },
    };
  }, [content]);
  const onEditorChange = (state: EditorState, editor: LexicalEditor) => {
    state.read(() => {
      updating.current = true;
      onChange?.(state.toJSON());
      updating.current = false;
    });
    // editor.update(() => {
    //   const html = $generateHtmlFromNodes(editor, null);
    //   setDebugHtml(html);
    // });
    if (onEmpty) {
      editor.update(() => {
        const state = editor.getEditorState();
        onEmpty(state.isEmpty ? state.isEmpty() : false);
      });
    }
  };

  useEffect(() => {}, []);
  placeholder ??= '';

  return (
    <>
      <LexicalComposer initialConfig={editorConfig}>
        {/* <Syncer content={content} last={state} updating={updating} /> */}
        <Clear refFn={clear} />
        <div className='relative mx-auto max-w-[600px] text-left font-normal leading-[20px] text-black'>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className='contenteditable relative m-0 min-h-[80px] resize-none rounded-[6px] border border-[#E0E0E0] bg-[#F8F8F8] px-3 py-2 text-small leading-normal caret-[#444] outline-none'
                aria-placeholder={placeholder}
                placeholder={
                  <div className='pointer-events-none absolute left-3 top-2 inline-block select-none overflow-hidden text-small text-[#A0A0A0]'>
                    {placeholder}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <BeautifulMentionsPlugin
            items={mentionItems}
            autoSpace
            allowSpaces
            mentionEnclosure='!'
            menuComponent={CustomMenu}
            menuItemComponent={CustomMenuItem}
          />
          {/* <LexicalTypeaheadMenuPlugin options={atItems} /> */}
          <OnChangePlugin onChange={onEditorChange} />
          <ClearEditorPlugin />
        </div>
      </LexicalComposer>
    </>
  );
}

function Clear({
  refFn,
}: {
  refFn?: MutableRefObject<(() => void) | undefined>;
}) {
  const [editor] = useLexicalComposerContext();

  // memoize the callback to break effect cycles & avoid re-renders
  const clear = React.useCallback(() => {
    const emptyState = editor.parseEditorState(
      JSON.stringify(emptyEditorState),
    );
    editor.setEditorState(emptyState);
  }, [editor]);

  useEffect(() => {
    if (refFn) {
      refFn.current = clear;
    }
  }, [refFn, clear]);

  return <></>;
}

const emptyEditorState = {
  root: {
    children: [
      {
        children: [
          {
            detail: 0,
            format: 0,
            mode: 'normal',
            style: '',
            text: '',
            type: 'text',
            version: 1,
          },
        ],
        direction: null,
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: null,
    format: '',
    indent: 0,
    type: 'root',
    version: 1,
  },
};
